/** Firestore operations for surety bond requests and the bonding settings doc. */

import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type DocumentData,
  type FieldValue,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import type {
  SuretyBondRequest,
  SuretyBondRequestStatus,
  SuretyBondingSettings,
  SuretyContractRange,
  SuretyContractSummaryEnclosure,
  SuretyEnclosureDocs,
  SuretyScheduleEnclosure,
  SuretySchedulePhase,
} from '../types/regulatory';
import { DEFAULT_SURETY_BONDING_SETTINGS, normalizeSuretyBondingSettings } from './suretyBondRequest';
import { removeUndefinedDeep } from './removeUndefinedDeep';

export const SURETY_BOND_REQUESTS = 'suretyBondRequests';
export const SURETY_BONDING_SETTINGS_DOC = 'suretyBonding';

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  const d = new Date(v as string);
  return Number.isNaN(d.getTime()) ? null : d;
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function num(v: unknown, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function ymdOrNull(v: unknown): string | null {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
}

function mapSchedule(raw: unknown): SuretyScheduleEnclosure | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const phases: SuretySchedulePhase[] = Array.isArray(d.phases)
    ? d.phases
      .filter((p: unknown): p is Record<string, unknown> => !!p && typeof p === 'object')
      .map((p): SuretySchedulePhase => ({
        key: str(p.key),
        name: str(p.name),
        startDate: ymdOrNull(p.startDate) ?? '',
        endDate: ymdOrNull(p.endDate) ?? '',
        source: p.source === 'tasks' || p.source === 'manual' ? p.source : 'simulated',
      }))
      .filter((p) => p.key && p.startDate && p.endDate)
    : [];
  if (!phases.length) return null;
  return { phases, note: str(d.note), builtAt: ymdOrNull(d.builtAt) ?? '' };
}

function mapContractSummary(raw: unknown): SuretyContractSummaryEnclosure | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const rows = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object') : []);
  return {
    signedBase: numOrNull(d.signedBase),
    currentContractValue: num(d.currentContractValue),
    changeOrders: rows(d.changeOrders).map((c) => ({
      number: num(c.number),
      title: str(c.title),
      amount: num(c.amount),
      approvedAt: ymdOrNull(c.approvedAt),
    })),
    installments: rows(d.installments).map((i) => ({
      id: str(i.id),
      label: str(i.label),
      percent: num(i.percent),
      amount: num(i.amount),
      dueDate: ymdOrNull(i.dueDate),
      estimated: i.estimated === true,
      basis: str(i.basis),
    })),
    builtAt: ymdOrNull(d.builtAt) ?? '',
  };
}

function mapContractRange(raw: unknown): SuretyContractRange | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const min = numOrNull(d.min);
  const max = numOrNull(d.max);
  if (min == null || max == null) return null;
  return { min, max, basis: str(d.basis) };
}

function mapEnclosureDocs(raw: unknown): SuretyEnclosureDocs | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const schedule = mapSchedule(d.schedule);
  const contractSummary = mapContractSummary(d.contractSummary);
  if (!schedule && !contractSummary) return null;
  return { schedule, contractSummary };
}

export function mapSuretyBondRequest(id: string, data: DocumentData): SuretyBondRequest {
  const obligee = (data.obligee && typeof data.obligee === 'object' ? data.obligee : {}) as Record<string, unknown>;
  const agent = (data.agent && typeof data.agent === 'object' ? data.agent : {}) as Record<string, unknown>;
  const signer = (data.signer && typeof data.signer === 'object' ? data.signer : {}) as Record<string, unknown>;
  const bondType = data.bondType === 'bid' ? 'bid' : 'performance_payment';
  const statusRaw = data.status;
  const status: SuretyBondRequestStatus =
    statusRaw === 'sent' || statusRaw === 'issued' || statusRaw === 'declined' ? statusRaw : 'draft';
  return {
    id,
    dealId: str(data.dealId),
    projectId: str(data.projectId),
    projectLabel: str(data.projectLabel),
    projectNumber: str(data.projectNumber),
    bondType,
    status,
    letterDate: ymdOrNull(data.letterDate) ?? '',
    contractAmount: num(data.contractAmount),
    contractRange: mapContractRange(data.contractRange),
    bondPercent: num(data.bondPercent),
    bondAmount: num(data.bondAmount),
    bidDate: ymdOrNull(data.bidDate),
    contractDate: ymdOrNull(data.contractDate),
    startDate: ymdOrNull(data.startDate),
    completionDate: ymdOrNull(data.completionDate),
    warrantyMonths: num(data.warrantyMonths, 12),
    obligee: {
      role: obligee.role === 'general_contractor' ? 'general_contractor' : 'owner',
      name: str(obligee.name),
      address: str(obligee.address),
    },
    siteAddress: str(data.siteAddress),
    scopeOfWork: str(data.scopeOfWork),
    bondForm: str(data.bondForm),
    neededBy: ymdOrNull(data.neededBy),
    retainagePercent: numOrNull(data.retainagePercent),
    liquidatedDamagesPerDay: numOrNull(data.liquidatedDamagesPerDay),
    architectEngineer: str(data.architectEngineer),
    subcontractedPercent: numOrNull(data.subcontractedPercent),
    designInScope: data.designInScope !== false,
    enclosures: Array.isArray(data.enclosures)
      ? data.enclosures.filter((e: unknown): e is string => typeof e === 'string' && e.trim().length > 0)
      : [],
    enclosureDocs: mapEnclosureDocs(data.enclosureDocs),
    notes: str(data.notes),
    agent: {
      agency: str(agent.agency),
      contact: str(agent.contact),
      email: str(agent.email),
      phone: str(agent.phone),
      address: str(agent.address),
    },
    signer: {
      name: str(signer.name),
      title: str(signer.title),
    },
    bondNumber: str(data.bondNumber),
    suretyName: str(data.suretyName),
    sentAt: toDate(data.sentAt),
    issuedAt: toDate(data.issuedAt),
    createdAt: toDate(data.createdAt),
    createdBy: str(data.createdBy),
    updatedAt: toDate(data.updatedAt),
    updatedBy: str(data.updatedBy),
  };
}

export function subscribeSuretyBondRequests(
  onRows: (rows: SuretyBondRequest[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(collection(db, SURETY_BOND_REQUESTS), orderBy('updatedAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => onRows(snap.docs.map((d) => mapSuretyBondRequest(d.id, d.data()))),
    (err) => onError?.(err as Error),
  );
}

export type SuretyBondRequestInput = Omit<
  SuretyBondRequest,
  'id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy' | 'sentAt' | 'issuedAt'
>;

/** Create (no id) or update (id) a request. Returns the document id. */
export async function saveSuretyBondRequest(
  input: SuretyBondRequestInput,
  opts: { id?: string; userId: string },
): Promise<string> {
  const ref = opts.id
    ? doc(db, SURETY_BOND_REQUESTS, opts.id)
    : doc(collection(db, SURETY_BOND_REQUESTS));
  const payload = removeUndefinedDeep({
    ...input,
    // Explicit null so a merge write clears enclosures the user removed.
    enclosureDocs: input.enclosureDocs ?? null,
    contractRange: input.contractRange ?? null,
    bondAmount: Math.round(input.bondAmount * 100) / 100,
    contractAmount: Math.round(input.contractAmount * 100) / 100,
    updatedAt: serverTimestamp(),
    updatedBy: opts.userId,
    ...(opts.id ? {} : { createdAt: serverTimestamp(), createdBy: opts.userId, sentAt: null, issuedAt: null }),
  });
  await setDoc(ref, payload, { merge: true });
  return ref.id;
}

export async function setSuretyBondRequestStatus(
  id: string,
  status: SuretyBondRequestStatus,
  opts: { userId: string; bondNumber?: string; suretyName?: string },
): Promise<void> {
  const patch: Record<string, FieldValue | string | null> = {
    status,
    updatedAt: serverTimestamp(),
    updatedBy: opts.userId,
  };
  if (status === 'sent') patch.sentAt = serverTimestamp();
  if (status === 'issued') {
    patch.issuedAt = serverTimestamp();
    if (opts.bondNumber !== undefined) patch.bondNumber = opts.bondNumber.trim();
    if (opts.suretyName !== undefined) patch.suretyName = opts.suretyName.trim();
  }
  await updateDoc(doc(db, SURETY_BOND_REQUESTS, id), patch);
}

/**
 * Permanently remove a request. Callers should re-mirror the deal's portal
 * bond afterwards (`mirrorSuretyBondToPortal`) so the client no longer sees it.
 */
export async function deleteSuretyBondRequest(id: string): Promise<void> {
  await deleteDoc(doc(db, SURETY_BOND_REQUESTS, id));
}

export function subscribeSuretyBondingSettings(
  onSettings: (settings: SuretyBondingSettings, exists: boolean) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, 'financeSettings', SURETY_BONDING_SETTINGS_DOC),
    (snap) => {
      if (!snap.exists()) {
        onSettings(DEFAULT_SURETY_BONDING_SETTINGS, false);
        return;
      }
      onSettings(normalizeSuretyBondingSettings(snap.data()), true);
    },
    (err) => onError?.(err as Error),
  );
}

export async function saveSuretyBondingSettings(
  settings: SuretyBondingSettings,
  userId: string,
): Promise<void> {
  await setDoc(
    doc(db, 'financeSettings', SURETY_BONDING_SETTINGS_DOC),
    removeUndefinedDeep({
      ...normalizeSuretyBondingSettings(settings),
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    }),
    { merge: true },
  );
}
